import { TaskEither } from "fp";

import * as Data from "~/Data";
import * as ELD from "~/ELD";
import { Request } from "~/Request";
import { WebAPI } from "~/WebAPI";

export namespace Resubmit {
  /**
   * Send a submission again.
   *
   * One mutation covers both stacks: for a migrated driver the API regenerates
   * the file from the original submission's inputs, and for one still on legacy
   * it proxies that stack's retransmit. Web passes the id and does not branch —
   * the ids look alike and the API resolves which store owns it.
   */
  export const request = ({
    id,
  }: Data.Reference.Reference): Request.Fn<
    ELD.Submission.Status.Failure.Resend.Resend
  > => (context) =>
    TaskEither.tryCatch(
      () =>
        WebAPI.clientFor(context)
          .mutate<
            WebAPI.ELD.ResubmitEldOutputFileResult,
            WebAPI.ELD.ResubmitEldOutputFileVariables
          >({
            mutation: WebAPI.ELD.ResubmitEldOutputFileDocument,
            variables: { submissionId: id },
          })
          .then(({ data }) => {
            const result = data?.resubmitEldOutputFile;

            // A resolved mutation carrying no result did not re-queue anything.
            // A `Right` here is read as a completed resend — the row drops its
            // error, the list refetches, and the submitter is shown a resend
            // that never happened.
            if (!result) {
              throw Error("resubmitEldOutputFile returned no result");
            }

            // The new submission's id, not the original's — a resubmit creates a
            // row of its own, which is why the page refetches after this lands.
            // A result that omits the id is contract drift rather than a
            // declined resend, and the original id keeps the refetch pointed at
            // a row that exists.
            return { id: result.submissionId ?? id };
          }),
      WebAPI.ServiceError.toError
    );
}
